import { inject, observer } from 'mobx-react';
import * as React from 'react';
import {
  AnalyticsTrigger,
  Autoscroll,
  Button,
  Card,
  CardContent,
  Grid,
  GridColumn,
  Heading,
  Image,
  Modal,
  ModalProvider,
  ModalTrigger,
  Notice,
  Text
} from 'telia-front-react';
import config from '../../Config';
import RoamingStore from '../../stores/RoamingStore';
import { Segment } from '../../stores/SegmentStore';
import SsoStore from '../../stores/SsoStore';
import { TranslationsContext } from '@teliaee/telia-translate';

interface EeaCountryCardProps {
  segment: Segment;
  isoCode: string;
  roamingStore?: RoamingStore;
  ssoStore?: SsoStore;
}

@observer
class EeaCountryCard extends React.Component<EeaCountryCardProps> {
  static contextType = TranslationsContext;
  declare context: React.ContextType<typeof TranslationsContext>;
  public render() {
    const translate = this.context.translate;
    const { segment, isoCode } = this.props;

    const countriesAnalyticsId = `Link - ${isoCode} - Vaata riike, kus kehtivad eesti hinnad ja tingimused`;
    const selfServiceAnalyticsId = `Link - SelfService - ${isoCode} - Vaatan iseteenindusest`;

    return (
      <Card padding="small" responsive={[{ breakPoint: 'md', props: { padding: 'xlarge' } }]}>
        <CardContent>
          <Grid>
            <GridColumn width={['md-6']}>
              <Text>
                <Heading element="h2">{this.getHeaderText()}</Heading>
                <p>{translate(`roaming.country.eea.sub.header.${segment}`)}</p>
                <AnalyticsTrigger trigger={{ id: selfServiceAnalyticsId, callback: 'onClick' }}>
                  <Button
                    className="text__margin-double"
                    text={translate('roaming.country.eea.selfservice-button.text')}
                    iconRight="#arrow-right"
                    url={config.mobileServicesUrl}
                    onClick={this.selfServiceClick}
                  />
                </AnalyticsTrigger>
                <p>
                  {translate('roaming.country.eea.pricing.text')} {this.getPricingButton()}.
                </p>
                <Notice size="small">{translate('roaming.country.eea.notice.text')}</Notice>
              </Text>
            </GridColumn>
            <GridColumn width={['md-6']}>
              <Grid align={['center-xs']}>
                <GridColumn width={['xs']}>
                  <Text className="text-center">
                    <Image
                      role="presentation"
                      // tslint:disable-next-line:max-line-length
                      srcset="//res.cloudinary.com/telia/image/upload/f_auto,fl_lossy,q_auto:best,w_540/v1537349178/tweb/roaming/map_2x 540w, //res.cloudinary.com/telia/image/upload/f_auto,fl_lossy,q_auto:best,w_1080/v1537349178/tweb/roaming/map_2x 1080w"
                      width={540}
                    />
                    <AnalyticsTrigger trigger={{ id: countriesAnalyticsId, callback: 'onOpen' }}>
                      <ModalProvider>
                        <ModalTrigger>
                          <Button className="text__margin-double" text={translate('roaming.country.eea.show-countries.button.text')} link={true} />
                        </ModalTrigger>
                        <Modal>
                          <Grid align={['center-xs']}>
                            <GridColumn width={['md-6']}>
                              <Text className="text-center">
                                {this.getEeaCountryList()} <br />
                                <br />
                                {translate('roaming.country.eea.no-internet.text')}
                                <br />
                                {translate('roaming.country.eea.no-internet.list')}
                              </Text>
                            </GridColumn>
                          </Grid>
                        </Modal>
                      </ModalProvider>
                    </AnalyticsTrigger>
                  </Text>
                </GridColumn>
              </Grid>
            </GridColumn>
          </Grid>
        </CardContent>
      </Card>
    );
  }

  private getHeaderText() {
    const translate = this.context.translate;
    const { segment, isoCode, roamingStore } = this.props;
    if (roamingStore.isMobileInternetCountry()) {
      return translate(`roaming.country.eea.main.header.text.${segment}`, {
        countryName: translate(`roaming.country.eea.main.header.${isoCode}`)
      });
    }
    return translate(`roaming.country.eea.no-internet.main.header.text.${segment}`, {
      countryName: translate(`roaming.country.eea.main.header.${isoCode}`)
    });
  }

  private getEeaCountryList() {
    return this.props.roamingStore.countryList.countries
      .filter((country) => {
        return country.eea;
      })
      .map((country) => country.name)
      .join(', ');
  }

  public selfServiceClick = (event: React.FormEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    event.preventDefault();
    this.props.ssoStore.ensureLoginWithGotoUrl(config.mobileServicesUrl);
  };

  private getPricingButton() {
    const translate = this.context.translate;
    const { isoCode } = this.props;
    const pricingAnalyticsId = `Link - ${isoCode} - Hinnakiri`;
    return (
      <AnalyticsTrigger trigger={{ id: pricingAnalyticsId, callback: 'onClick' }}>
        <Autoscroll id="operators" speed={1000} offset={150}>
          <Button text={translate('roaming.country.eea.pricing.button')} link={true} isUnderlined={false} />
        </Autoscroll>
      </AnalyticsTrigger>
    );
  }
}

export default inject('roamingStore', 'ssoStore')(EeaCountryCard);
